import type {
  EmployeeFinancialData,
  EmployeeRateHistory,
  SalaryMultiplier,
  PayrollMonthlyRecord,
  SalaryOverviewRecord,
  EmployeeLoan,
  EmployeeLoanPayment,
  EmployeeAdvance,
  PaginatedResponse,
  PayrollLAConfig,
  LoanDeductionSchedule,
  LoanDeductionsView,
} from '@pms/shared';
import type {
  UpsertFinancialDataInput,
  BulkUpdateMultipliersInput,
  CreateLoanInput,
  UpdateLoanInput,
  LoanStatusInput,
  PayEmiInput,
  CreateAdvanceInput,
  UpdateAdvanceInput,
  ListLoansQuery,
  ListAdvancesQuery,
  UpdatePayrollLAConfigInput,
  SetLoanDeductionScheduleInput,
  GenerateLoanScheduleInput,
} from '@pms/shared';
import { api } from '@/lib/api';

export const payrollService = {
  // ── Financial Data ───────────────────────────────────────────────────────────

  async getFinancialData(userId: string): Promise<EmployeeFinancialData | null> {
    const res = await api.get<{ data: EmployeeFinancialData | null }>(`/payroll/financial/${userId}`);
    return res.data.data;
  },

  async upsertFinancialData(userId: string, input: UpsertFinancialDataInput): Promise<EmployeeFinancialData> {
    const res = await api.put<{ data: EmployeeFinancialData }>(`/payroll/financial/${userId}`, input);
    return res.data.data;
  },

  // ── Rate History ─────────────────────────────────────────────────────────────

  async getRateHistory(userId: string): Promise<EmployeeRateHistory[]> {
    const res = await api.get<{ data: EmployeeRateHistory[] }>(`/payroll/rate-history/${userId}`);
    return res.data.data;
  },

  async getMyRateHistory(): Promise<EmployeeRateHistory[]> {
    const res = await api.get<{ data: EmployeeRateHistory[] }>('/payroll/rate-history/me');
    return res.data.data;
  },

  // ── Salary pass key (own) ────────────────────────────────────────────────────

  /** Whether this employee has already been issued a key. Never carries the key itself. */
  async getMySalaryKeyStatus(): Promise<{ exists: boolean; created_at?: string }> {
    const res = await api.get<{ data: { exists: boolean; created_at?: string } }>(
      '/payroll/salary-key/me',
    );
    return res.data.data;
  },

  /**
   * Set this employee's 6-digit salary PIN. Nothing comes back: the PIN is the
   * credential and the employee is the one who keeps it.
   */
  async setMySalaryPin(pin: string): Promise<void> {
    await api.post('/payroll/salary-key/me', { pin });
  },

  /**
   * Check a PIN and get back the key it unlocks. Throws a 401 when the PIN is wrong.
   *
   * The key is what the salary requests that follow are priced with — the server keeps
   * none of its own — so the caller has to hold on to it for the length of the unlock.
   */
  async unlockMySalary(pin: string): Promise<string> {
    const res = await api.post<{ data: { passkey: string } }>(
      '/payroll/salary-key/me/unlock', { pin },
    );
    return res.data.data.passkey;
  },

  // ── Salary Multipliers Config ────────────────────────────────────────────────

  async getMultipliers(): Promise<SalaryMultiplier[]> {
    const res = await api.get<{ data: SalaryMultiplier[] }>('/payroll/config/multipliers');
    return res.data.data;
  },

  async updateMultipliers(input: BulkUpdateMultipliersInput): Promise<SalaryMultiplier[]> {
    const res = await api.put<{ data: SalaryMultiplier[] }>('/payroll/config/multipliers', input);
    return res.data.data;
  },

  // ── Loans & Advances Config ──────────────────────────────────────────────────

  async getLAConfig(): Promise<PayrollLAConfig> {
    const res = await api.get<{ data: PayrollLAConfig }>('/payroll/config/loans-advances');
    return res.data.data;
  },

  async updateLAConfig(input: UpdatePayrollLAConfigInput): Promise<PayrollLAConfig> {
    const res = await api.patch<{ data: PayrollLAConfig }>('/payroll/config/loans-advances', input);
    return res.data.data;
  },

  // ── Salary (self) ────────────────────────────────────────────────────────────

  async getMySalaryHistory(): Promise<PayrollMonthlyRecord[]> {
    const res = await api.get<{ data: PayrollMonthlyRecord[] }>('/payroll/salary/me');
    return res.data.data;
  },

  async getMyMonthSalary(year: number, month: number): Promise<PayrollMonthlyRecord | null> {
    const res = await api.get<{ data: PayrollMonthlyRecord | null }>(`/payroll/salary/me/${year}/${month}`);
    return res.data.data;
  },

  // ── Salary (admin) ───────────────────────────────────────────────────────────

  async getStaffSalaryHistory(userId: string): Promise<PayrollMonthlyRecord[]> {
    const res = await api.get<{ data: PayrollMonthlyRecord[] }>(`/payroll/salary/${userId}`);
    return res.data.data;
  },

  async getStaffMonthSalary(userId: string, year: number, month: number): Promise<PayrollMonthlyRecord | null> {
    const res = await api.get<{ data: PayrollMonthlyRecord | null }>(`/payroll/salary/${userId}/${year}/${month}`);
    return res.data.data;
  },

  // ── Loans ────────────────────────────────────────────────────────────────────

  async listLoans(params?: Partial<ListLoansQuery>): Promise<PaginatedResponse<EmployeeLoan>> {
    const res = await api.get<PaginatedResponse<EmployeeLoan>>('/payroll/loans', { params });
    return res.data;
  },

  async getMyLoans(): Promise<EmployeeLoan[]> {
    const res = await api.get<{ data: EmployeeLoan[] }>('/payroll/loans/me');
    return res.data.data;
  },

  async getMyLoanDeductions(loanId: string): Promise<LoanDeductionsView> {
    const res = await api.get<{ data: LoanDeductionsView }>(`/payroll/loans/me/${loanId}/deductions`);
    return res.data.data;
  },

  async getMyAdvances(): Promise<EmployeeAdvance[]> {
    const res = await api.get<{ data: EmployeeAdvance[] }>('/payroll/advances/me');
    return res.data.data;
  },

  async createLoan(input: CreateLoanInput): Promise<EmployeeLoan> {
    const res = await api.post<{ data: EmployeeLoan }>('/payroll/loans', input);
    return res.data.data;
  },

  async updateLoan(id: string, input: UpdateLoanInput): Promise<EmployeeLoan> {
    const res = await api.patch<{ data: EmployeeLoan }>(`/payroll/loans/${id}`, input);
    return res.data.data;
  },

  async updateLoanStatus(id: string, input: LoanStatusInput): Promise<EmployeeLoan> {
    const res = await api.patch<{ data: EmployeeLoan }>(`/payroll/loans/${id}/status`, input);
    return res.data.data;
  },

  async payEmi(loanId: string, input: PayEmiInput): Promise<EmployeeLoanPayment> {
    const res = await api.post<{ data: EmployeeLoanPayment }>(`/payroll/loans/${loanId}/pay-emi`, input);
    return res.data.data;
  },

  async getLoanSchedule(loanId: string): Promise<LoanDeductionSchedule[]> {
    const res = await api.get<{ data: LoanDeductionSchedule[] }>(`/payroll/loans/${loanId}/schedule`);
    return res.data.data;
  },

  async setLoanScheduleEntry(loanId: string, input: SetLoanDeductionScheduleInput): Promise<LoanDeductionSchedule> {
    const res = await api.post<{ data: LoanDeductionSchedule }>(`/payroll/loans/${loanId}/schedule`, input);
    return res.data.data;
  },

  async deleteLoanScheduleEntry(loanId: string, entryId: string): Promise<void> {
    await api.delete(`/payroll/loans/${loanId}/schedule/${entryId}`);
  },

  async generateLoanSchedule(loanId: string, input: GenerateLoanScheduleInput): Promise<LoanDeductionSchedule[]> {
    const res = await api.post<{ data: LoanDeductionSchedule[] }>(`/payroll/loans/${loanId}/schedule/generate`, input);
    return res.data.data;
  },

  async getLoanDeductions(loanId: string): Promise<LoanDeductionsView> {
    const res = await api.get<{ data: LoanDeductionsView }>(`/payroll/loans/${loanId}/deductions`);
    return res.data.data;
  },

  async getLoanPayments(loanId: string): Promise<EmployeeLoanPayment[]> {
    const res = await api.get<{ data: EmployeeLoanPayment[] }>(`/payroll/loans/${loanId}/payments`);
    return res.data.data;
  },

  // ── Advances ─────────────────────────────────────────────────────────────────

  async listAdvances(params?: Partial<ListAdvancesQuery>): Promise<PaginatedResponse<EmployeeAdvance>> {
    const res = await api.get<PaginatedResponse<EmployeeAdvance>>('/payroll/advances', { params });
    return res.data;
  },

  async createAdvance(input: CreateAdvanceInput): Promise<EmployeeAdvance> {
    const res = await api.post<{ data: EmployeeAdvance }>('/payroll/advances', input);
    return res.data.data;
  },

  async updateAdvance(id: string, input: UpdateAdvanceInput): Promise<EmployeeAdvance> {
    const res = await api.patch<{ data: EmployeeAdvance }>(`/payroll/advances/${id}`, input);
    return res.data.data;
  },

  async cancelAdvance(id: string): Promise<void> {
    await api.patch(`/payroll/advances/${id}/cancel`);
  },

  // ── Salary Overview ──────────────────────────────────────────────────────────

  async listSalaryOverview(params: {
    year: number;
    month: number;
    page?: number;
    limit?: number;
    q?: string;
  }): Promise<PaginatedResponse<SalaryOverviewRecord>> {
    const res = await api.get<PaginatedResponse<SalaryOverviewRecord>>(
      '/payroll/salary/overview',
      { params },
    );
    return res.data;
  },

  async exportSalaryOverview(params: { year: number; month: number; q?: string }): Promise<void> {
    const res = await api.get('/payroll/salary/overview/export', { params, responseType: 'blob' });
    const url = URL.createObjectURL(new Blob([res.data as BlobPart], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `salary-overview-${params.year}-${String(params.month).padStart(2, '0')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  },
};
